import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useIntl } from '../../../../util/reactIntl';
import css from './GetAppModal.module.css';

const QR_TARGET =
  'https://play.google.com/store/apps/details?id=com.now.customer&hl=en';
const QR_SRC = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(
  QR_TARGET
)}`;

const storeLinks = {
  customer: {
    ios: 'https://apps.apple.com/cn/app/now-offers/id6742392519',
    android: 'https://play.google.com/store/apps/details?id=com.now.customer&hl=en',
  },
  merchant: {
    ios: 'https://apps.apple.com/in/app/now-offers-merchants/id6742392597',
    android: 'https://play.google.com/store/apps/details?id=com.now.merchant&hl=en',
  },
};

const AppleIcon = () => (
  <svg viewBox="0 0 384 512" className={css.storeIcon} aria-hidden>
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM256.6 84.5c19.4-23 17.7-43.9 17.1-51.4-17.2 1-37.1 11.7-48.5 24.9-12.6 14.1-20 31.6-18.4 50.9 18.6 1.4 35.7-8.1 49.8-24.4z" />
  </svg>
);

const GooglePlayIcon = () => (
  <svg viewBox="0 0 512 512" className={css.storeIcon} aria-hidden>
    <path d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

const StoreRow = ({ label, ios, android, intl }) => (
  <div className={css.storeRow}>
    <div className={css.storeLabel}>{label}</div>
    <div className={css.storeButtons}>
      <a href={ios} target="_blank" rel="noopener noreferrer" className={css.storeButton}>
        <AppleIcon />
        <span>{intl.formatMessage({ id: 'TopbarDesktop.appStore' })}</span>
      </a>
      <a href={android} target="_blank" rel="noopener noreferrer" className={css.storeButton}>
        <GooglePlayIcon />
        <span>{intl.formatMessage({ id: 'TopbarDesktop.googlePlay' })}</span>
      </a>
    </div>
  </div>
);

/**
 * Get App modal — visual reference: Header.tsx Dialog in NOWOffers_WebsiteCode.
 */
const GetAppModal = ({ isOpen, onClose, arabicLanguage }) => {
  const intl = useIntl();

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyUp = e => {
      if (e.key === 'Escape') onClose();
    };
    document.body.addEventListener('keyup', onKeyUp);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.removeEventListener('keyup', onKeyUp);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={css.overlay} role="presentation" onClick={onClose}>
      <div
        className={classNames(css.dialog, arabicLanguage && css.arabicDirection)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="get-app-title"
        onClick={e => e.stopPropagation()}
      >
        <button type="button" className={css.closeButton} onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
            <path
              d="M18 6 6 18M6 6l12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <h2 id="get-app-title" className={css.title}>
          {intl.formatMessage({ id: 'TopbarDesktop.getAppModalTitle' })}
        </h2>
        <p className={css.description}>
          {intl.formatMessage({ id: 'TopbarDesktop.getAppModalDescription' })}
        </p>

        <div className={css.qrBlock}>
          <div className={css.qrFrame}>
            <img
              src={QR_SRC}
              alt={intl.formatMessage({ id: 'TopbarDesktop.getAppQrAlt' })}
              width={200}
              height={200}
            />
          </div>
          <p className={css.qrHint}>
            {intl.formatMessage({ id: 'TopbarDesktop.getAppScanHint' })}
          </p>
        </div>

        <div className={css.storeSections}>
          <StoreRow
            label={intl.formatMessage({ id: 'TopbarDesktop.getAppCustomerLabel' })}
            ios={storeLinks.customer.ios}
            android={storeLinks.customer.android}
            intl={intl}
          />
          <StoreRow
            label={intl.formatMessage({ id: 'TopbarDesktop.getAppMerchantLabel' })}
            ios={storeLinks.merchant.ios}
            android={storeLinks.merchant.android}
            intl={intl}
          />
        </div>
      </div>
    </div>
  );
};

export default GetAppModal;
